// bigwa-icons.tsx
// Sistema de ícones customizados do Bigwa
// Substitui ícones Lucide por ícones com identidade visual própria
// Padrão: stroke #000 1.5px round, accents #477b72 #f2bc41 #D4918A
// Gerado em: março 2026

import React from 'react';

export type IconVariant = 'light' | 'dark' | 'active';

interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  variant?: IconVariant;
}

const getStroke = (v: IconVariant = 'light') => v === 'light' ? '#000' : '#E8E3DA';
const getAccent = (v: IconVariant = 'light') => v === 'active' ? 'white' : v === 'dark' ? '#45A08F' : '#477b72';

// ╔══════════════════════════════════════════════════════════════╗
// ║  ÍCONES BIGWA — FAMÍLIA APROVADA v1.0 (05/03/2026)          ║
// ║  NÃO ALTERAR sem instrução explícita de Adrian               ║
// ║  Qualquer mudança exige aprovação visual prévia              ║
// ║  Referência: INSTRUCAO-CC-ICONES-v12.md                     ║
// ╚══════════════════════════════════════════════════════════════╝

// ─────────────────────────────────────────────
// BLOCO 1 — NAVEGAÇÃO / SIDEBAR
// ─────────────────────────────────────────────

export const HomeIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M3 10.5L12 3l9 7.5V20a2 2 0 01-2 2H5a2 2 0 01-2-2V10.5z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <circle cx="12" cy="15" r="2.5" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
  </svg>
);

export const ProjectsIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M3 8a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M3 8a2 2 0 012-2h4l2 2" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

export const LibraryIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* lombada fina — esquerda */}
    <path d="M2.5 19 L2.5 6 Q2.5 4.5 4 4.5 Q5.5 4.5 5.5 6 L5.5 19 Z"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
    {/* spine colorido na lombada fina */}
    <line x1="4" y1="5" x2="4" y2="19"
      stroke="#477B72" strokeWidth="1.5" strokeLinecap="round"/>
    {/* livro central — fill semântico #477B72 */}
    <path d="M8 19 L8 5 Q8 3.5 10 3.5 Q12 3.5 12 5 L12 19 Z"
      fill="#477B72" opacity="0.22" stroke="#1a2e2a" strokeWidth="1.8" strokeLinejoin="round"/>
    {/* título simulado no livro central */}
    <line x1="9" y1="8" x2="11" y2="8"
      stroke="#477B72" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="9" y1="10" x2="11" y2="10"
      stroke="#477B72" strokeWidth="1.2" strokeLinecap="round"/>
    {/* livro largo — direita */}
    <path d="M14 19 L14 7 Q14 5.5 16 5.5 Q19 5.5 19 7 L19 19 Z"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
    {/* lombada extra fina — extrema direita */}
    <path d="M20.5 19 L20.5 8 Q20.5 7 21.5 7 L21.5 19 Z"
      stroke="#1a2e2a" strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const RepositoryIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const isDark = variant === 'dark' || variant === 'active';
  const s = isDark ? 'white' : '#1a2e2a';
  const f = isDark ? 'rgba(79,173,158,0.35)' : 'rgba(79,173,158,0.22)';
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* pedra grande base */}
    <ellipse cx="12" cy="18" rx="5" ry="3.2"
      fill={f} stroke={s} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"/>
    {/* pedra esquerda */}
    <ellipse cx="6.5" cy="10.5" rx="3.8" ry="2.8"
      fill="none" stroke={s} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"/>
    {/* pedra direita */}
    <ellipse cx="17" cy="9.5" rx="3" ry="2.2"
      fill="none" stroke={s} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
  );
};

export const LeiturasIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M15 3l4 4h-4V3z" fill="#477b72" stroke="#477b72" strokeWidth="1" strokeLinejoin="round"/>
    <rect x="4" y="10" width="16" height="3.5" fill="#477b72"/>
    <line x1="7" y1="16" x2="17" y2="16" stroke="#1A3530" strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="7" y1="18.5" x2="13" y2="18.5" stroke="#1A3530" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

export const ResearchDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="12" width="4" height="9" rx="1" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="10" y="7" width="4" height="14" rx="1" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="17" y="3" width="4" height="18" rx="1" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="3" y1="21" x2="21" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const FavoritesIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="#f2bc41" stroke="#f2bc41" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

export const ConnectionsIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <circle cx="12" cy="4" r="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="12" y1="6" x2="12" y2="9" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="20" r="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="12" y1="15" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="4" cy="12" r="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="6" y1="12" x2="9" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="20" cy="12" r="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="15" y1="12" x2="18" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const CitationsIcon = ({ size = 24, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4.5 13 Q4.5 8.5 8 8.5 Q8 11 6 12 Q7.5 12 7.5 14.5 Q7.5 17 5.2 17 Q2.8 17 2.8 14.5 Q2.8 13 4.5 13Z"
      fill="#7B68EE" opacity="0.22"/>
    <path d="M4.5 13 Q4.5 8.5 8 8.5 Q8 11 6 12 Q7.5 12 7.5 14.5 Q7.5 17 5.2 17 Q2.8 17 2.8 14.5 Q2.8 13 4.5 13Z"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M15.5 13 Q15.5 8.5 19 8.5 Q19 11 17 12 Q18.5 12 18.5 14.5 Q18.5 17 16.2 17 Q13.8 17 13.8 14.5 Q13.8 13 15.5 13Z"
      fill="#7B68EE" opacity="0.22"/>
    <path d="M15.5 13 Q15.5 8.5 19 8.5 Q19 11 17 12 Q18.5 12 18.5 14.5 Q18.5 17 16.2 17 Q13.8 17 13.8 14.5 Q13.8 13 15.5 13Z"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinejoin="round"/>
    <circle cx="6.5" cy="10.5" r="0.9" fill="#7B68EE"/>
    <circle cx="17.5" cy="10.5" r="0.9" fill="#7B68EE"/>
  </svg>
);

export const ArchiveIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="10" width="20" height="11" rx="2" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="2" y="6" width="20" height="5" rx="2" fill={a} stroke={a} strokeWidth="1.5"/>
    <line x1="9" y1="15.5" x2="15" y2="15.5" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
  );
};

export const TrashIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <line x1="3" y1="6" x2="21" y2="6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6z" fill="#D4918A" stroke="#D4918A" strokeWidth="1.5" strokeLinejoin="round"/>
    <line x1="10" y1="11" x2="10" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="14" y1="11" x2="14" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const PreferencesIcon = ({ size = 20, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#477b72" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

export const ColaboracaoIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* figura de trás — outline suave */}
    <circle cx="16.5" cy="6" r="3" stroke="#1a2e2a" strokeWidth="1.4" opacity="0.5"/>
    <path d="M20 20.5v-1.5A3.5 3.5 0 0 0 18 15.8"
      stroke="#1a2e2a" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
    {/* figura da frente — fill semântico #477B72 */}
    <circle cx="9" cy="7.5" r="3.5" fill="#477B72" opacity="0.18"/>
    <circle cx="9" cy="7.5" r="3.5" stroke="#1a2e2a" strokeWidth="1.8"/>
    <circle cx="9" cy="7.5" r="1.6" fill="#477B72"/>
    <path d="M2.5 21v-1.5A4 4 0 0 1 6.5 15.5h5A4 4 0 0 1 15.5 19.5V21"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

export const AdminIcon = ({ size = 24, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M3 21 C3 17 5.5 15 9 15 C12.5 15 15 17 15 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="18" cy="8" r="3" stroke="currentColor" strokeWidth="1.6"/>
    <circle cx="18" cy="8" r="1.2" fill="currentColor" fillOpacity="0.5"/>
    <line x1="18" y1="4" x2="18" y2="3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="18" y1="12" x2="18" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="14" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="22" y1="8" x2="23" y2="8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="15.17" y1="5.17" x2="14.46" y2="4.46" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20.83" y1="10.83" x2="21.54" y2="11.54" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20.83" y1="5.17" x2="21.54" y2="4.46" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="15.17" y1="10.83" x2="14.46" y2="11.54" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 2 — BARRA DE FERRAMENTAS
// ─────────────────────────────────────────────

export const AddIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="12" y1="8" x2="12" y2="16" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="12" x2="16" y2="12" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ImportIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 3v13" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <polyline points="7,11 12,16 17,11" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M5 20h14" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ExportIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 16V3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <polyline points="7,8 12,3 17,8" stroke="#f2bc41" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M5 20h14" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const FilterIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <line x1="4" y1="6" x2="20" y2="6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="12" x2="17" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="10" y1="18" x2="14" y2="18" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const SortIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <line x1="4" y1="6" x2="14" y2="6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="4" y1="12" x2="12" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="4" y1="18" x2="10" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <polyline points="18,4 21,7 18,10" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="21" y1="7" x2="16" y2="7" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const SearchIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={s} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 2l6 6h-6V2z" fill={a} stroke={a} strokeWidth="1" strokeLinejoin="round"/>
    <circle cx="10" cy="14.5" r="3" stroke={s} strokeWidth="1.4" fill="none"/>
    <circle cx="10" cy="14.5" r="1.2" fill={a}/>
    <line x1="12.5" y1="17" x2="15" y2="19.5" stroke={s} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
  );
};

export const GlobalSearchIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="10" cy="10" r="6.5" stroke={s} strokeWidth="1.6" fill="none"/>
    <line x1="15" y1="15" x2="21" y2="21" stroke={s} strokeWidth="2" strokeLinecap="round"/>
    <path d="M11 7 L8.5 10.5 H11 L9 14" stroke={a} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
  );
};

export const MoreIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="5" r="1.5" fill="#000"/>
    <circle cx="12" cy="12" r="1.5" fill="#477b72"/>
    <circle cx="12" cy="19" r="1.5" fill="#000"/>
  </svg>
);

export const InspectorIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="3" width="20" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="15" y1="3" x2="15" y2="21" stroke="#1A3530" strokeWidth="1.5"/>
    <rect x="15" y="3" width="7" height="18" rx="0" fill="#477b72" stroke="none"/>
    <path d="M15 3h5a2 2 0 012 2v14a2 2 0 01-2 2h-5" fill="#477b72" stroke="none"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 2 — UI GERAL
// ─────────────────────────────────────────────

export const CloseIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <line x1="5" y1="5" x2="19" y2="19" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="19" y1="5" x2="5" y2="19" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
  );
};

export const BackIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polyline points="15,18 9,12 15,6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const ForwardIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polyline points="9,18 15,12 9,6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const MenuIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <line x1="3" y1="6" x2="21" y2="6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="12" x2="21" y2="12" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="18" x2="21" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const EditIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={s} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" fill={a} stroke={a} strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
  );
};

export const DeleteIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polyline points="3,6 5,6 21,6" stroke="#D4918A" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="#D4918A" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" stroke="#D4918A" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const CopyIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="8" y="8" width="13" height="13" rx="2" stroke={s} strokeWidth="1.5" fill="none"/>
    <path d="M5 16H4a2 2 0 01-2-2V4a2 2 0 012-2h10a2 2 0 012 2v1" stroke={a} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
  </svg>
  );
};

export const SaveIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke={s} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M7 3v5h10V3" stroke={a} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <rect x="7" y="3" width="10" height="5" rx="0" fill={a} stroke="none"/>
    <rect x="9" y="13" width="6" height="8" rx="1" stroke={s} strokeWidth="1.5" fill="none"/>
  </svg>
  );
};

export const CancelIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="9" y1="9" x2="15" y2="15" stroke="#D4918A" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="15" y1="9" x2="9" y2="15" stroke="#D4918A" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const PdfIndicatorIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <rect x="5" y="13" width="14" height="6" rx="1" fill="#D4918A" stroke="none"/>
    <text x="7.5" y="18" fontFamily="sans-serif" fontSize="4.5" fontWeight="700" fill="#fff">PDF</text>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 3 — ESTADOS E STATUS
// ─────────────────────────────────────────────

// Protagonista: patinha de pássaro com check
export const ReadStatusIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="9" r="4" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="12" y1="13" x2="9.5" y2="17.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="13" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="13" x2="14.5" y2="17.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="13.5" x2="10" y2="15.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <polyline points="9.5,9 11.2,11 14.5,7.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const UnreadStatusIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12" r="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
  </svg>
);

export const IncompleteIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#f2bc41" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="8" r="1" fill="#f2bc41"/>
    <line x1="12" y1="11" x2="12" y2="16" stroke="#f2bc41" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const LoadingIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#e0e0e0" strokeWidth="1.5" fill="none"/>
    <path d="M12 3a9 9 0 019 9" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
  </svg>
);

export const SuccessIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <polyline points="7.5,12 10.5,15 16.5,9" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const ErrorIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" fill="#D4918A" stroke="#D4918A" strokeWidth="1.5"/>
    <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="15.5" y1="8.5" x2="8.5" y2="15.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 3 — INSPECTOR (ABAS)
// ─────────────────────────────────────────────

export const InfoIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke={s} strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="8" r="1" fill={a}/>
    <line x1="12" y1="11" x2="12" y2="16" stroke={a} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
  );
};

export const SiEmptyIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" stroke={s} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <circle cx="12" cy="12" r="3" stroke={s} strokeWidth="1.4" fill="none"/>
    <circle cx="12" cy="12" r="1.3" fill={a}/>
    <path d="M16 8 L20 4" stroke={a} strokeWidth="1.6" strokeLinecap="round"/>
    <polyline points="17,4 20,4 20,7" stroke={a} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
  );
};

export const NotesIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="4" y="3" width="12" height="15" rx="2" stroke={s} strokeWidth="1.5" fill="none"/>
    <line x1="7" y1="8" x2="13" y2="8" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="11" x2="13" y2="11" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="14" x2="10" y2="14" stroke={s} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M14 14 Q18 10 20 7 Q17 9 14 14z" fill="#f2bc41" stroke="#f2bc41" strokeWidth="0.8" strokeLinejoin="round"/>
    <line x1="14" y1="14" x2="17" y2="9.5" stroke={s} strokeWidth="1" strokeLinecap="round"/>
  </svg>
  );
};

export const FichamentoIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke={s} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M15 3l4 4h-4V3z" fill={a} stroke={a} strokeWidth="1" strokeLinejoin="round"/>
    <rect x="4" y="9" width="16" height="3" rx="0" fill={a}/>
    <line x1="7" y1="15" x2="17" y2="15" stroke={s} strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="7" y1="18" x2="13" y2="18" stroke={s} strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
  );
};

export const CaptureGraphIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="7" width="20" height="14" rx="3" stroke={s} strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="14" r="4" stroke={s} strokeWidth="1.4" fill="none"/>
    <line x1="9.5" y1="16" x2="9.5" y2="14.5" stroke={a} strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="11.5" y1="16" x2="11.5" y2="12.5" stroke={a} strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="13.5" y1="16" x2="13.5" y2="13.5" stroke={a} strokeWidth="1.3" strokeLinecap="round"/>
    <rect x="8" y="4" width="5" height="3.5" rx="1.5" stroke={s} strokeWidth="1.3" fill="none"/>
  </svg>
  );
};

export const TagsIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M3 7a2 2 0 012-2h7.17a2 2 0 011.42.59l6 6a2 2 0 010 2.82l-4.58 4.58a2 2 0 01-2.82 0l-6-6A2 2 0 013 11.41V7z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <circle cx="8" cy="10" r="1.5" fill="#477b72"/>
  </svg>
);

export const AttachIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M21 11.5l-9.9 9.9a6 6 0 01-8.49-8.49l9.9-9.9a4 4 0 015.66 5.66L8.29 18.54a2 2 0 01-2.83-2.83l9.2-9.2" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M21 11.5l-9.9 9.9a6 6 0 01-8.49-8.49" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
  </svg>
);

export const LinkReferenceIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke={s} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke={a} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
  );
};

// ─────────────────────────────────────────────
// BLOCO 4 — TIPOS DE REFERÊNCIA
// ─────────────────────────────────────────────

export const BookRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4 4a2 2 0 012-2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="13,2 13,7 18,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <line x1="7" y1="13" x2="17" y2="13" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="16.5" x2="17" y2="16.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="10" x2="11" y2="10" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ArticleRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="7" y1="8" x2="17" y2="8" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="11.5" x2="17" y2="11.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="15" x2="13" y2="15" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ChapterRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 6V20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 5c0-1 .8-1.5 2-1.5 1.5 0 4 1 6 2.5V20c-2-1.5-4.5-2.5-6-2.5C4.8 17.5 4 17 4 16V5z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M20 5c0-1-.8-1.5-2-1.5-1.5 0-4 1-6 2.5V20c2-1.5 4.5-2.5 6-2.5 1.2 0 2-.5 2-1.5V5z" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

export const ThesisRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polygon points="12,4 22,9 12,14 2,9" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M6 11.5v5c0 0 2.5 2.5 6 2.5s6-2.5 6-2.5v-5" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="22" y1="9" x2="22" y2="15" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="22" cy="15.5" r="1" fill="#f2bc41"/>
  </svg>
);

export const ConferenceRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="9" y="3" width="6" height="10" rx="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9" y="3" width="6" height="5" rx="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 11a7 7 0 0014 0" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="18" x2="12" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9" y1="21" x2="15" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ReportRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="3" width="14" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="7" y1="8" x2="13" y2="8" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="11.5" x2="13" y2="11.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="15" x2="11" y2="15" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="8" y="1" width="6" height="4" rx="1" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
  </svg>
);

export const WebsiteRefIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <path d="M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="12" x2="21" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 4 — RELEVÂNCIA
// ─────────────────────────────────────────────

export const RelevanceCentralIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" fill="#3D5A6C" stroke="#3D5A6C" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="4" fill="white"/>
    <circle cx="12" cy="12" r="2" fill="#3D5A6C"/>
  </svg>
);

export const RelevanceImportantIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" fill="#8FB3C4" stroke="#8FB3C4" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="4" fill="white" opacity={0.6}/>
  </svg>
);

export const RelevanceDoubtIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#A8C4D4" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12" r="4" stroke="#A8C4D4" strokeWidth="1.5" strokeDasharray="2 2" fill="none"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO 5 — DADOS DE PESQUISA
// ─────────────────────────────────────────────

export const InterviewRecordingIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="9" y="2" width="6" height="11" rx="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9" y="2" width="6" height="5.5" rx="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 10a7 7 0 0014 0" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="17" x2="12" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9" y1="20" x2="15" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 8.5c-.8 1-.8 2.5 0 3.5" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M21 8.5c.8 1 .8 2.5 0 3.5" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
  </svg>
);

export const AudioFieldIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="9" y="2" width="6" height="11" rx="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9" y="2" width="6" height="5.5" rx="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 10a7 7 0 0014 0" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="17" x2="12" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9" y1="20" x2="15" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M1 19l2-3 2 1.5L7 14" stroke="#477b72" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const AudioBioacousticIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="9" y="2" width="6" height="11" rx="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9" y="2" width="6" height="5.5" rx="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 10a7 7 0 0014 0" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="17" x2="12" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M1 13 Q3 11 5 13" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M19 13 Q21 11 23 13" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
  </svg>
);

export const OralHistoryIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="8" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12" r="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="12" y1="4" x2="12" y2="9" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="15" x2="12" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="4" y1="12" x2="9" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="15" y1="12" x2="20" y2="12" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 2 Q12 0 15 2" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
  </svg>
);

export const VideoFieldIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="6" width="14" height="12" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M16 10l5-3v10l-5-3V10z" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M4 14l3-4 3 3" stroke="#1A3530" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const VideoBehaviorIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="6" width="14" height="12" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M16 10l5-3v10l-5-3V10z" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5 12 Q8 9.5 11 12 Q8 14.5 5 12z" stroke="#1A3530" strokeWidth="1" strokeLinejoin="round" fill="none"/>
    <circle cx="8" cy="12" r="1" fill="#000"/>
  </svg>
);

export const VideoExperimentIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="6" width="14" height="12" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M16 10l5-3v10l-5-3V10z" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M9 8l-3 5h4l-3 5" stroke="#f2bc41" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const PhotoFieldIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="5" width="20" height="15" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="4" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="1.5" fill="#477b72"/>
    <path d="M7 5V3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M17 5V3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 16l3-4 3 3 2-2 3 3" stroke="#1A3530" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const PhotoSpecimenIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="5" width="20" height="15" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="4" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="1.5" fill="#477b72"/>
    <line x1="9" y1="7" x2="7" y2="5" stroke="#477b72" strokeWidth="1" strokeLinecap="round"/>
    <line x1="15" y1="7" x2="17" y2="5" stroke="#477b72" strokeWidth="1" strokeLinecap="round"/>
    <circle cx="7" cy="4.5" r="0.7" fill="#477b72"/>
    <circle cx="17" cy="4.5" r="0.7" fill="#477b72"/>
  </svg>
);

export const PhotoHistoricalIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="5" width="20" height="15" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="4" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12.5" r="1.5" fill="#477b72"/>
    <line x1="2" y1="5" x2="6" y2="9" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="22" y1="5" x2="18" y2="9" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

export const MicroscopyIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="8" r="4" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="8" r="1.5" fill="#477b72"/>
    <line x1="12" y1="12" x2="12" y2="17" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 17h8" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 17v2h4v-2" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="12" y1="4" x2="12" y2="2" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="2" x2="15" y2="2" stroke="#f2bc41" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const MedicalImagingIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M8 8 Q12 6 16 8" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M10 11 Q12 10 14 11" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M9 13.5 Q12 12 15 13.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M9.5 16 Q12 15 14.5 16" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="8" x2="12" y2="18" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const DocumentSourceIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <line x1="8" y1="11" x2="16" y2="11" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="14" x2="16" y2="14" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="17" x2="12" y2="17" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const DocumentLegalIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <line x1="12" y1="9" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="11" x2="16" y2="11" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M8 11 Q6.5 13 8 15" stroke="#477b72" strokeWidth="1" strokeLinecap="round" fill="none"/>
    <path d="M16 11 Q17.5 13 16 15" stroke="#477b72" strokeWidth="1" strokeLinecap="round" fill="none"/>
  </svg>
);

export const DocumentAdminIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <rect x="8" y="10" width="8" height="6" rx="1" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <rect x="9.5" y="8.5" width="5" height="2" rx="0.5" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <line x1="8" y1="17.5" x2="16" y2="17.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const DocumentHistoricalIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M6 3 Q4 3 4 5v14 Q4 21 6 21h13a1 1 0 001-1V7l-4-4H6z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M4 5 Q4 8 7 8 Q4 8 4 11" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <line x1="8" y1="12" x2="15" y2="12" stroke="#f2bc41" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="15" x2="15" y2="15" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="18" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const PeriodicalIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="4" width="18" height="16" rx="1.5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="3" y="4" width="18" height="6" rx="1.5" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="3" y1="13" x2="21" y2="13" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <rect x="6" y="15" width="5" height="3" rx="0.5" stroke="#1A3530" strokeWidth="1" fill="none"/>
    <line x1="13" y1="15" x2="18" y2="15" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="13" y1="17" x2="18" y2="17" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

export const DossierIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M3 8a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M3 8a2 2 0 012-2h4l2 2" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
    <line x1="8" y1="13" x2="16" y2="13" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="15.5" x2="14" y2="15.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const ClinicalRecordIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <rect x="8" y="12.5" width="8" height="3" rx="0.5" fill="#D4918A"/>
    <rect x="10" y="10" width="4" height="8" rx="0.5" fill="#D4918A"/>
  </svg>
);

export const AttendanceRecordIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <rect x="7" y="10" width="10" height="8" rx="1" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <line x1="7" y1="13" x2="17" y2="13" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <circle cx="10" cy="11.5" r="0.7" fill="#477b72"/>
    <circle cx="14" cy="11.5" r="0.7" fill="#477b72"/>
    <line x1="9" y1="15" x2="15" y2="15" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

export const CourtDecisionIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="17" width="11" height="3" rx="1.5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="8" y="7" width="10" height="5" rx="1.5" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="12" y1="12" x2="6" y2="17" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const LegislationIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="3" y="4" width="18" height="5" rx="2" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="12" y1="12" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="13.5" x2="16" y2="13.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M8 13.5 Q6.5 15.5 8 17.5" stroke="#1A3530" strokeWidth="1" strokeLinecap="round" fill="none"/>
    <path d="M16 13.5 Q17.5 15.5 16 17.5" stroke="#1A3530" strokeWidth="1" strokeLinecap="round" fill="none"/>
  </svg>
);

export const StatisticsJudicialIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="12" width="4" height="9" rx="1" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="10" y="8" width="4" height="13" rx="1" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="17" y="3" width="4" height="18" rx="1" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="3" y1="21" x2="21" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const SensorDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="7" y="7" width="10" height="10" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <line x1="10" y1="7" x2="10" y2="4" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="14" y1="7" x2="14" y2="4" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="10" y1="17" x2="10" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="14" y1="17" x2="14" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="10" x2="4" y2="10" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="7" y1="14" x2="4" y2="14" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="17" y1="10" x2="20" y2="10" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="17" y1="14" x2="20" y2="14" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const OccurrenceDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="10" r="5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="10" r="2" fill="#477b72"/>
    <path d="M12 15v6" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6 20 Q9 18 12 21 Q15 18 18 20" stroke="#1A3530" strokeWidth="1" strokeLinecap="round" fill="none"/>
  </svg>
);

export const GenomicDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M8 4 Q12 8 16 12 Q12 16 8 20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <path d="M16 4 Q12 8 8 12 Q12 16 16 20" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="10" y1="7" x2="14" y2="7" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="9" y1="12" x2="15" y2="12" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="10" y1="17" x2="14" y2="17" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const GeospatialIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <path d="M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
    <line x1="3" y1="12" x2="21" y2="12" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="16" cy="8" r="2" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <line x1="16" y1="10" x2="16" y2="13" stroke="#477b72" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const SimulationDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="4" width="20" height="14" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M7 10l3 3-3 3" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="13" y1="15" x2="17" y2="15" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="6" y1="20" x2="18" y2="20" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ExperimentalDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M9 3h6v10l3 5a2 2 0 01-1.73 3H7.73A2 2 0 016 18l3-5V3z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <line x1="9" y1="8" x2="15" y2="8" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M7 17h10" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="11" cy="16" r="1.5" fill="#477b72"/>
    <circle cx="15" cy="15" r="1" fill="#477b72"/>
  </svg>
);

export const SampleCollectionIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="10" y="3" width="4" height="14" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="10" y="11" width="4" height="6" rx="2" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <line x1="8" y1="3" x2="16" y2="3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 19 Q12 22 17 19" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
  </svg>
);

export const LabReportIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <polyline points="7,12 9,14 13,10" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="8" y1="16.5" x2="16" y2="16.5" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const LabResultIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="4" y="3" width="3" height="12" rx="1.5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="4" y="9" width="3" height="6" rx="1.5" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <line x1="4" y1="3" x2="7" y2="3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <polyline points="10,17 13,11 16,14 19,8" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="10" y1="20" x2="22" y2="20" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="10" y1="8" x2="10" y2="20" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const PhysiologicalSignalIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <polyline points="2,12 5,12 7,7 9,17 11,10 13,14 15,12 22,12" stroke="#D4918A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const FieldDiaryIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="5" y="3" width="14" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="5" y1="7" x2="19" y2="7" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="11" x2="16" y2="11" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="14" x2="16" y2="14" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="17" x2="13" y2="17" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="3" cy="7" r="1" stroke="#1A3530" strokeWidth="1" fill="none"/>
    <circle cx="3" cy="12" r="1" stroke="#1A3530" strokeWidth="1" fill="none"/>
    <circle cx="3" cy="17" r="1" stroke="#1A3530" strokeWidth="1" fill="none"/>
  </svg>
);

export const ObservationNotesIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M1 12 Q6 5 12 5 Q18 5 23 12 Q18 19 12 19 Q6 19 1 12z" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="12" r="3" fill="#477b72" stroke="#477b72" strokeWidth="1"/>
    <circle cx="12" cy="12" r="1" fill="white"/>
  </svg>
);

export const TranscriptionIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="9" y="2" width="6" height="9" rx="3" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="9" y="2" width="6" height="4.5" rx="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 9a7 7 0 0014 0" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="12" y1="16" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="5" y1="20" x2="19" y2="20" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="5" y1="22" x2="14" y2="22" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const AnamnesisIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="6" r="3" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M5 20c0-3.86 3.13-7 7-7s7 3.14 7 7" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="7" y1="15" x2="17" y2="15" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="8" y1="17.5" x2="16" y2="17.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const EpidemiologicalDbIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <ellipse cx="12" cy="5" rx="8" ry="2.5" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <path d="M4 5v5c0 1.38 3.58 2.5 8 2.5S20 11.38 20 10V5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <path d="M4 10v5c0 1.38 3.58 2.5 8 2.5S20 16.38 20 15v-5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <polyline points="6,17 8,17 9,15 11,19 13,16 14,17 18,17" stroke="#D4918A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

export const QuestionnaireIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="4" y="3" width="16" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="8" y1="8" x2="16" y2="8" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="7" y="11" width="3" height="3" rx="0.5" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <polyline points="7.5,12.5 8.5,13.5 10,11.5" stroke="#477b72" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <rect x="7" y="16" width="3" height="3" rx="0.5" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <line x1="12" y1="12.5" x2="16" y2="12.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="12" y1="17.5" x2="16" y2="17.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const HealthQuestionnaireIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="4" y="3" width="16" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="8" y1="8" x2="16" y2="8" stroke="#D4918A" strokeWidth="1.5" strokeLinecap="round"/>
    <rect x="7" y="11" width="3" height="3" rx="0.5" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <polyline points="7.5,12.5 8.5,13.5 10,11.5" stroke="#D4918A" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <rect x="7" y="16" width="3" height="3" rx="0.5" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <line x1="8.5" y1="16.8" x2="8.5" y2="18.2" stroke="#D4918A" strokeWidth="1" strokeLinecap="round"/>
    <line x1="7.8" y1="17.5" x2="9.2" y2="17.5" stroke="#D4918A" strokeWidth="1" strokeLinecap="round"/>
    <line x1="12" y1="12.5" x2="16" y2="12.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="12" y1="17.5" x2="16" y2="17.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const MapSketchIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <line x1="9" y1="3" x2="9" y2="18" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="15" y1="6" x2="15" y2="21" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="6" cy="11" r="1.5" fill="#477b72"/>
  </svg>
);

export const IconographicIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="6" y="6" width="12" height="12" rx="1" stroke="#1A3530" strokeWidth="1" strokeDasharray="2 1.5" fill="none"/>
    <circle cx="12" cy="12" r="2.5" fill="#f2bc41" stroke="#f2bc41" strokeWidth="1"/>
    <line x1="12" y1="8.5" x2="12" y2="7.5" stroke="#f2bc41" strokeWidth="1" strokeLinecap="round"/>
    <line x1="14.5" y1="9.5" x2="15.2" y2="8.8" stroke="#f2bc41" strokeWidth="1" strokeLinecap="round"/>
    <line x1="15.5" y1="12" x2="16.5" y2="12" stroke="#f2bc41" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

export const TechnicalDrawingIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4 4h16v3H4z" fill="#477b72" stroke="#477b72" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M4 4v16" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="4" x2="8" y2="7" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="12" y1="4" x2="12" y2="7" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="16" y1="4" x2="16" y2="7" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="4" y1="8" x2="7" y2="8" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="4" y1="12" x2="7" y2="12" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="4" y1="16" x2="7" y2="16" stroke="#1A3530" strokeWidth="1" strokeLinecap="round"/>
    <line x1="8" y1="20" x2="20" y2="8" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ProtocolIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="3" y="3" width="18" height="5" rx="2" fill="#477b72" stroke="#477b72" strokeWidth="1.5"/>
    <line x1="7" y1="12" x2="17" y2="12" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="7" y1="15.5" x2="17" y2="15.5" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="7" y1="19" x2="13" y2="19" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const TechnicalReportIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 3h10l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <polyline points="15,3 15,7 19,7" stroke="#1A3530" strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="12" cy="14" r="3" stroke="#1A3530" strokeWidth="1.2" fill="none"/>
    <circle cx="12" cy="14" r="1" fill="#477b72"/>
    <line x1="12" y1="10" x2="12" y2="11" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="12" y1="17" x2="12" y2="18" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="9" y1="14" x2="10" y2="14" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
    <line x1="14" y1="14" x2="15" y2="14" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const SourceCodeIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="3" width="20" height="18" rx="2" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <rect x="2" y="3" width="20" height="5" rx="2" fill="#1a1a1a" stroke="#1a1a1a" strokeWidth="1.5"/>
    <circle cx="6" cy="5.5" r="0.8" fill="#D4918A"/>
    <circle cx="9" cy="5.5" r="0.8" fill="#f2bc41"/>
    <circle cx="12" cy="5.5" r="0.8" fill="#477b72"/>
    <path d="M6 13l3 2-3 2" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="13" y1="17" x2="18" y2="17" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const SurveyMappingIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="10" r="5" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <line x1="12" y1="5" x2="12" y2="3" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12" y1="15" x2="12" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="7" y2="10" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="10" r="2" fill="#477b72"/>
    <line x1="9" y1="21" x2="15" y2="21" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="8" y1="19" x2="16" y2="19" stroke="#1A3530" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const OtherDataIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="#1A3530" strokeWidth="1.5" fill="none"/>
    <circle cx="12" cy="8" r="1" fill="#000"/>
    <line x1="12" y1="11" x2="12" y2="17" stroke="#1A3530" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9" y1="17" x2="15" y2="17" stroke="#477b72" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const FocusIcon = ({ size = 24, ...props }: IconProps) => {
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* haste vertical */}
    <line x1="7" y1="5" x2="7" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* traço topo */}
    <line x1="7" y1="5" x2="17" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* traço meio: coral — o ponto de foco */}
    <line x1="7" y1="12" x2="14" y2="12" stroke="#D4918A" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
  );
};

// ─────────────────────────────────────────────
// LEITURAS PAGE ICONS — Exclusivos da página Leituras
// ─────────────────────────────────────────────

export const ReferenciasIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="4" width="5" height="16" rx="1" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="3" y="4" width="2" height="16" rx="1" fill={a} stroke={a} strokeWidth="1"/>
    <rect x="9.5" y="4" width="5" height="16" rx="1" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="9.5" y="4" width="2" height="16" rx="1" fill={a} stroke={a} strokeWidth="1"/>
    <rect x="16" y="4" width="5" height="16" rx="1" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="16" y="4" width="2" height="16" rx="1" fill={a} stroke={a} strokeWidth="1"/>
  </svg>
  );
};

export const HorizonteIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const _s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M10.5 11 Q7 7.5 1 9" stroke={a} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M10.5 13.5 Q6 11 1 13.5" stroke={a} strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.65"/>
    <path d="M10.5 16 Q7 14.5 3 17" stroke={a} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.35"/>
    <path d="M13.5 11 Q17 7.5 23 9" stroke={a} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M13.5 13.5 Q18 11 23 13.5" stroke={a} strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.65"/>
    <path d="M13.5 16 Q17 14.5 21 17" stroke={a} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.35"/>
    <circle cx="12" cy="12" r="2.2" fill={a}/>
  </svg>
  );
};

export const SelecionarIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="10" width="14" height="11" rx="2" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="5" y="7" width="14" height="11" rx="2" stroke={s} strokeWidth="1.3" fill="white" opacity="0.9"/>
    <rect x="7" y="4" width="14" height="11" rx="2" fill="#f5faf9" stroke={a} strokeWidth="1.5"/>
    <polyline points="10,10 13,13 19,7" stroke={a} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
  );
};

// ─────────────────────────────────────────────
// TEMPLATE ICONS — Criação de projeto
// ─────────────────────────────────────────────

export const TemplatePesquisaIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={s} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 2l6 6h-6V2z" fill={a} stroke={a} strokeWidth="1" strokeLinejoin="round"/>
    <circle cx="10" cy="14.5" r="3" stroke={s} strokeWidth="1.4" fill="none"/>
    <circle cx="10" cy="14.5" r="1.2" fill={a}/>
    <line x1="12.5" y1="17" x2="15" y2="19.5" stroke={s} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
  );
};

export const AulasSeminariosIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="6" cy="6" r="2.5" stroke={s} strokeWidth="1.5" fill="none"/>
    <path d="M2 18c0-3 1.8-5 4-5" stroke={s} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <circle cx="18" cy="6" r="2.5" stroke={s} strokeWidth="1.5" fill="none"/>
    <path d="M22 18c0-3-1.8-5-4-5" stroke={s} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <path d="M12 13v6" stroke={a} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 13c0-.8.7-1.2 2-1.4 1.5 0 2.5.7 3 1.4v6c-.5-.7-1.5-1.3-3-1.4C7.7 17.5 7 17.2 7 16.5V13z" stroke={a} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    <path d="M17 13c0-.8-.7-1.2-2-1.4-1.5 0-2.5.7-3 1.4v6c.5-.7 1.5-1.3 3-1.4 1.3-.1 2-.4 2-1.1V13z" stroke={a} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
  </svg>
  );
};

/** @deprecated Use AulasSeminariosIcon */
export const TemplateLeituraIcon = AulasSeminariosIcon;

export const TemplatePersonalizadoIcon = ({ size = 24, variant = 'light', ...props }: IconProps) => {
  const s = getStroke(variant); const a = getAccent(variant);
  return (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="2" width="10" height="10" rx="2" fill={a} stroke={a} strokeWidth="1"/>
    <rect x="14" y="2" width="8" height="4" rx="1.5" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="14" y="8" width="8" height="4" rx="1.5" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="2" y="14" width="5" height="8" rx="1.5" stroke={s} strokeWidth="1.5" fill="none"/>
    <rect x="9" y="14" width="13" height="8" rx="1.5" stroke={s} strokeWidth="1.5" fill="none"/>
  </svg>
  );
};

const workspaceTypeMap = {
  pesquisa: TemplatePesquisaIcon,
  leitura: AulasSeminariosIcon,
  personalizado: TemplatePersonalizadoIcon,
} as const;

export const WorkspaceTypeIcon = ({ type, ...props }: IconProps & { type: 'pesquisa' | 'leitura' | 'personalizado' }) => {
  const Icon = workspaceTypeMap[type];
  return <Icon {...props} />;
};

// ─────────────────────────────────────────────
// EXPORT MAP — para uso programático
// Permite substituição por ID string (ex: iconMap['read-status'])
// ─────────────────────────────────────────────

export const BigwaIconMap: Record<string, React.ComponentType<IconProps>> = {
  // Sidebar
  'home': HomeIcon,
  'projects': ProjectsIcon,
  'library': LibraryIcon,
  'repository': RepositoryIcon,
  'leituras': LeiturasIcon,
  'research-data': ResearchDataIcon,
  'favorites': FavoritesIcon,
  'connections': ConnectionsIcon,
  'citations': CitationsIcon,
  'archive': ArchiveIcon,
  'trash': TrashIcon,
  'preferences': PreferencesIcon,
  'colaboracao': ColaboracaoIcon,
  'admin': AdminIcon,
  // Toolbar
  'add': AddIcon,
  'import': ImportIcon,
  'export': ExportIcon,
  'filter': FilterIcon,
  'sort': SortIcon,
  'search': SearchIcon,
  'more': MoreIcon,
  'inspector': InspectorIcon,
  // UI Geral
  'close': CloseIcon,
  'back': BackIcon,
  'forward': ForwardIcon,
  'menu': MenuIcon,
  'edit': EditIcon,
  'delete': DeleteIcon,
  'copy': CopyIcon,
  'save': SaveIcon,
  'cancel': CancelIcon,
  'pdf-indicator': PdfIndicatorIcon,
  // Estados
  'read-status': ReadStatusIcon,
  'unread-status': UnreadStatusIcon,
  'incomplete': IncompleteIcon,
  'loading': LoadingIcon,
  'success': SuccessIcon,
  'error': ErrorIcon,
  // Inspector abas
  'info': InfoIcon,
  'notes': NotesIcon,
  'fichamento': FichamentoIcon,
  'tags': TagsIcon,
  'attach': AttachIcon,
  'link-reference': LinkReferenceIcon,
  // Tipos de referência
  'book': BookRefIcon,
  'article': ArticleRefIcon,
  'chapter': ChapterRefIcon,
  'thesis': ThesisRefIcon,
  'conference': ConferenceRefIcon,
  'report': ReportRefIcon,
  'website': WebsiteRefIcon,
  // Relevância
  'relevance-central': RelevanceCentralIcon,
  'relevance-important': RelevanceImportantIcon,
  'relevance-doubt': RelevanceDoubtIcon,
  // Dados de Pesquisa
  'interview-recording': InterviewRecordingIcon,
  'audio-field': AudioFieldIcon,
  'audio-bioacoustic': AudioBioacousticIcon,
  'oral-history': OralHistoryIcon,
  'video-field': VideoFieldIcon,
  'video-behavior': VideoBehaviorIcon,
  'video-experiment': VideoExperimentIcon,
  'photo-field': PhotoFieldIcon,
  'photo-specimen': PhotoSpecimenIcon,
  'photo-historical': PhotoHistoricalIcon,
  'microscopy': MicroscopyIcon,
  'medical-imaging': MedicalImagingIcon,
  'document-source': DocumentSourceIcon,
  'document-legal': DocumentLegalIcon,
  'document-admin': DocumentAdminIcon,
  'document-historical': DocumentHistoricalIcon,
  'periodical': PeriodicalIcon,
  'dossier': DossierIcon,
  'clinical-record': ClinicalRecordIcon,
  'attendance-record': AttendanceRecordIcon,
  'court-decision': CourtDecisionIcon,
  'legislation': LegislationIcon,
  'statistics-judicial': StatisticsJudicialIcon,
  'sensor-data': SensorDataIcon,
  'occurrence-data': OccurrenceDataIcon,
  'genomic-data': GenomicDataIcon,
  'geospatial': GeospatialIcon,
  'simulation-data': SimulationDataIcon,
  'experimental-data': ExperimentalDataIcon,
  'sample-collection': SampleCollectionIcon,
  'lab-report': LabReportIcon,
  'lab-result': LabResultIcon,
  'physiological-signal': PhysiologicalSignalIcon,
  'field-diary': FieldDiaryIcon,
  'observation-notes': ObservationNotesIcon,
  'transcription': TranscriptionIcon,
  'anamnesis': AnamnesisIcon,
  'epidemiological-db': EpidemiologicalDbIcon,
  'questionnaire': QuestionnaireIcon,
  'health-questionnaire': HealthQuestionnaireIcon,
  'map-sketch': MapSketchIcon,
  'iconographic': IconographicIcon,
  'technical-drawing': TechnicalDrawingIcon,
  'protocol': ProtocolIcon,
  'technical-report': TechnicalReportIcon,
  'source-code': SourceCodeIcon,
  'survey-mapping': SurveyMappingIcon,
  'other': OtherDataIcon,
};

// ─────────────────────────────────────────────
// BLOCO — BIBLIOTECA (Browser, Painel de Controle)
// ─────────────────────────────────────────────

export const BrowserIcon = ({ size = 14, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="2" y="3" width="6" height="18" rx="1.5" stroke="#477b72" strokeWidth="1.4" fill="rgba(71,123,114,0.08)"/>
    <rect x="9" y="3" width="6" height="18" rx="1.5" stroke="#1A3530" strokeWidth="1.2" fill="none" opacity="0.3"/>
    <rect x="16" y="3" width="6" height="18" rx="1.5" stroke="#1A3530" strokeWidth="1.2" fill="none" opacity="0.3"/>
    <line x1="3.5" y1="9" x2="7" y2="9" stroke="#477b72" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const PainelControleIcon = ({ size = 16, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="3" y="4" width="4" height="16" rx="2" fill="#477b72" opacity="0.2"/>
    <rect x="10" y="4" width="4" height="16" rx="2" fill="#477b72" opacity="0.2"/>
    <rect x="17" y="4" width="4" height="16" rx="2" fill="#477b72" opacity="0.2"/>
    <circle cx="5" cy="9" r="3.5" fill="#477b72"/>
    <circle cx="12" cy="6" r="3.5" fill="#477b72"/>
    <circle cx="19" cy="14" r="3.5" fill="#477b72"/>
  </svg>
);

// ─────────────────────────────────────────────
// BLOCO — SIDEBAR IDENTIDADE BIGUÁ
// ─────────────────────────────────────────────

export const LeiturasBiguaIcon = ({ size = 20, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* peixe caindo */}
    <ellipse cx="19" cy="4.5" rx="2.4" ry="1.1"
      fill="white" stroke="#1a2e2a" strokeWidth="0.9" transform="rotate(-22 19 4.5)"/>
    <circle cx="20.5" cy="3.7" r="0.5" fill="#1a2e2a"/>
    <path d="M17 3.2 L15.8 1.8 L15.2 3.5 Z" fill="#1a2e2a"/>
    {/* gotas */}
    <circle cx="16.5" cy="5.8" r="0.5" fill="#55B3C5" opacity="0.5"/>
    <circle cx="18" cy="7" r="0.42" fill="#55B3C5" opacity="0.38"/>
    {/* corpo do biguá */}
    <path d="M4 20 Q3.5 15.5 6 13 Q8 11 10.5 11.5"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    {/* cabeça preta */}
    <circle cx="11.5" cy="10.8" r="2.9" fill="#1a2e2a"/>
    {/* iridescência verde */}
    <path d="M9 9.5 Q11.5 7.5 14 9.5"
      stroke="#477B72" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.8"/>
    {/* olho turquesa */}
    <circle cx="12.5" cy="10" r="1" fill="#55B3C5"/>
    <circle cx="12.75" cy="9.75" r="0.38" fill="white"/>
    {/* bico superior */}
    <path d="M14.2 10.2 Q17.5 8.2 19.5 6.5"
      stroke="#1a2e2a" strokeWidth="2" strokeLinecap="round"/>
    {/* bico inferior */}
    <path d="M14.2 11.2 Q17 10.5 18.5 9.5"
      stroke="#1a2e2a" strokeWidth="1.5" strokeLinecap="round"/>
    {/* boca aberta — fill semântico #55B3C5 */}
    <path d="M14.2 10.2 Q17.5 8.2 19.5 6.5 Q18.5 10 14.2 11.2 Z"
      fill="#55B3C5" opacity="0.9"/>
  </svg>
);

export const DadosPesquisaIcon = ({ size = 20, ...props }: Omit<IconProps, 'variant'>) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    {/* galhos do ninho */}
    <path d="M2 17.5 Q5.5 15 8 16 Q10.5 17 12 16 Q13.5 15 16.5 16 Q19 17 22 15"
      stroke="#1a2e2a" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    <path d="M3 20 Q6.5 18 9 19 Q11.5 20 14.5 19 Q17 18 21.5 19.5"
      stroke="#1a2e2a" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
    {/* suportes verticais */}
    <path d="M7.5 21.5 Q7 19 8 16" stroke="#1a2e2a" strokeWidth="1.1" strokeLinecap="round" opacity="0.55"/>
    <path d="M12 21.5 Q11.5 19 12 16" stroke="#1a2e2a" strokeWidth="1.1" strokeLinecap="round" opacity="0.55"/>
    <path d="M16.5 21.5 Q17.5 19 16.5 16" stroke="#1a2e2a" strokeWidth="1.1" strokeLinecap="round" opacity="0.55"/>
    {/* 3 ovos — fill #F2BC41 protagonistas */}
    <ellipse cx="8" cy="13" rx="2.7" ry="2" fill="#F2BC41"/>
    <ellipse cx="8" cy="13" rx="2.7" ry="2" stroke="#1a2e2a" strokeWidth="1.4"/>
    <ellipse cx="12" cy="12" rx="2.7" ry="2" fill="#F2BC41"/>
    <ellipse cx="12" cy="12" rx="2.7" ry="2" stroke="#1a2e2a" strokeWidth="1.4"/>
    <ellipse cx="16" cy="13" rx="2.5" ry="1.9" fill="#F2BC41"/>
    <ellipse cx="16" cy="13" rx="2.5" ry="1.9" stroke="#1a2e2a" strokeWidth="1.4"/>
    {/* brilho nos ovos */}
    <ellipse cx="7.2" cy="12.2" rx="0.85" ry="0.55" fill="white" opacity="0.42" transform="rotate(-25 7.2 12.2)"/>
    <ellipse cx="11.2" cy="11.2" rx="0.85" ry="0.55" fill="white" opacity="0.42" transform="rotate(-25 11.2 11.2)"/>
    <ellipse cx="15.2" cy="12.2" rx="0.75" ry="0.5" fill="white" opacity="0.42" transform="rotate(-25 15.2 12.2)"/>
  </svg>
);

// ─────────────────────────────────────────────
// MINHAS OBRAS — Ícone para pill da Biblioteca
// Documento com linha de autoria em negrito (opção D aprovada)
// ─────────────────────────────────────────────

export const MinhasObrasIcon = ({ size = 22, active = false, ...props }: IconProps & { active?: boolean }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <rect x="3" y="3" width="18" height="18" rx="4" stroke={active ? '#477B72' : '#C4C4C6'} strokeWidth="1.5" fill={active ? '#477B72' : 'transparent'} />
    <text x="12" y="17" textAnchor="middle" fontSize="14" fontWeight="600" fontFamily="system-ui, -apple-system, sans-serif" fill={active ? 'white' : '#C4C4C6'}>A</text>
  </svg>
);

export default BigwaIconMap;
